import { useSelector } from 'react-redux';
import { useMatch } from 'react-router-dom';
import { useGetSiteQuery } from '../apiRtk/be4feApi';
import { SITE_CATEGORY } from '../config/sites';
import { Roles } from '../modules/commonTypes';
import { selectors } from '../modules/sites';
import { getCurrentUserSelector } from '../modules/user/selectors';
import { useGetActualCompanyId } from './companyHooks';
import type { IUserRoles } from './rolesTypes';

export function useGetActualSiteId() {
  return useSelector(selectors.getSiteId);
}

export function useGetResolvedSiteId() {
  const companyId = useGetActualCompanyId();
  const storeSiteId = useGetActualSiteId();
  const siteMatch = useMatch('/companies/:companyId/sites/:siteId/*');
  const urlCompanyId = siteMatch?.params?.companyId ? Number.parseInt(siteMatch.params.companyId, 10) : undefined;
  const urlSiteId = siteMatch?.params?.siteId ? Number.parseInt(siteMatch.params.siteId, 10) : undefined;
  return storeSiteId ?? (urlCompanyId === companyId ? urlSiteId : undefined);
}

export function useIsAcomSite() {
  const companyId = useGetActualCompanyId();
  const siteId = useGetResolvedSiteId();

  const currentUser = useSelector(getCurrentUserSelector) as IUserRoles;
  const currentSiteFromUser = currentUser?.companies
    ?.find((c) => c.id === companyId)
    ?.sites?.find((s) => s.id === siteId);
  const isAcomFromUser = currentSiteFromUser?.siteType === SITE_CATEGORY.ACCESS_COMMANDER_REMOTE;

  const skipSiteQuery =
    !siteId || (isAcomFromUser && !currentSiteFromUser?.roles?.some((r) => r === Roles.ADMIN || r === Roles.STANDARD));

  const { data: siteData } = useGetSiteQuery({ companyId, siteId }, { skip: skipSiteQuery });

  return isAcomFromUser || (siteData?.siteCategory as string) === SITE_CATEGORY.ACCESS_COMMANDER_REMOTE;
}

export function useGetCurrentUserSiteRoles(companyId?: number, siteId?: number) {
  const currentUser = useSelector(getCurrentUserSelector) as {
    companies: { id: number; sites: { id: number; roles: Roles[] }[] }[];
  };

  if (companyId === undefined || siteId === undefined) {
    return [];
  }

  return currentUser.companies?.find((company) => company.id === companyId)?.sites?.find((site) => site.id === siteId)
    ?.roles;
}
